import {useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {CatalogoKairos} from '@/components/catalogo-kairos'
import {PerfilesCargoFiltro, type FiltroPropuestas} from '@/components/perfil-cargo-filtro'
import {PropuestaCargo} from '@/components/perfil-cargo-propuesta'
import {PorQue} from '@/components/ui/por-que'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Skeleton} from '@/components/ui/skeleton'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  PROPUESTAS_PAGE_SIZE,
  useAplicarEmparejamiento,
  usePropuestas,
  type DuplicadoCargo,
  type Emparejamientos,
} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {translateApiError} from '@/lib/errors'
import {SearchInput} from '@/components/ui/search-input'

function pendienteDe(filtro: FiltroPropuestas): string | undefined {
  if (filtro === 'pendientes') return 'true'
  if (filtro === 'resueltos') return 'false'
  return undefined
}

/** Sin primario de página: el acto vive en cada fila. Los avisos de CIIU y clase de riesgo van
 * acá porque son precondición de la propuesta, no del listado de cargos. */
export function PerfilCargoPropuestas({
  businessId,
  cabecera,
  puedeAplicar,
  razonBloqueo,
}: {
  businessId: string
  cabecera: Emparejamientos
  puedeAplicar: boolean
  razonBloqueo: string | null | undefined
}) {
  const {t} = useTranslation()
  const table = useTableState('cargo')
  const [filtro, setFiltro] = useState<FiltroPropuestas>('pendientes')
  const lista = usePropuestas(businessId, {
    page: table.page,
    search: table.search,
    extra: {pendiente: pendienteDe(filtro)},
  })
  const aplicar = useAplicarEmparejamiento(businessId)
  const [error, setError] = useState<string | null>(null)

  function usar(positionId: string, accion: 'aceptar' | 'cambiar' | 'descartar', catalogoKey?: string) {
    setError(null)
    aplicar.mutate({positionId, accion, catalogoKey}, {onError: (err) => setError(translateApiError(err))})
  }

  return (
    <div className="space-y-4" data-testid="propuestas">
      {!puedeAplicar && razonBloqueo && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="perfiles-cargo-bloqueo">
          {razonBloqueo}
        </p>
      )}
      {error && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="perfiles-cargo-error">
          {error}
        </p>
      )}

      {cabecera.pide_actividad && (
        <section className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="pide-actividad">
          <p>{t('perfilCargo.pideActividad')}</p>
          <span className="flex flex-wrap items-center gap-1.5">
            <Link className="underline" to={`/businesses/${businessId}/profile`}>
              {t('perfilCargo.pideActividadEnlace')}
            </Link>
            <PorQue label={t('common.porQue')} testId="pide-actividad-porque">
              <p>{t('perfilCargo.pideActividadPorque')}</p>
            </PorQue>
          </span>
        </section>
      )}
      {/* Aparte del CIIU: cambiar de actividad limpia la clase de riesgo, así que este estado es el normal. */}
      {cabecera.pide_clase_riesgo && (
        <section className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="pide-clase-riesgo">
          <p>{t('perfilCargo.pideClaseRiesgo')}</p>
          <span className="flex flex-wrap items-center gap-1.5">
            <Link className="underline" to={`/businesses/${businessId}/profile`}>
              {t('perfilCargo.pideActividadEnlace')}
            </Link>
            <PorQue label={t('common.porQue')} testId="pide-clase-riesgo-porque">
              <p>{t('perfilCargo.pideClaseRiesgoPorque')}</p>
            </PorQue>
          </span>
        </section>
      )}
      {!cabecera.pide_actividad && !cabecera.pide_clase_riesgo && cabecera.catalogo_sin_perfiles && (
        <div className="notice-amber space-y-1 rounded-lg px-3 py-2 text-sm" data-testid="catalogo-sin-perfiles">
          <p>{t('perfilCargo.catalogoSinPerfiles')}</p>
          <PorQue label={t('common.porQue')} testId="catalogo-sin-perfiles-porque">
            <p>{t('perfilCargo.catalogoSinPerfilesPorque')}</p>
          </PorQue>
        </div>
      )}

      {cabecera.duplicados.length > 0 && (
        <section className="surface rounded-lg p-4" data-testid="duplicados-nomina">
          <h2 className="font-heading text-base tracking-tight">{t('perfilCargo.duplicados.title')}</h2>
          <ul className="mt-2 space-y-2">
            {cabecera.duplicados.map((grupo: DuplicadoCargo) => (
              <li key={grupo.clave.join('-')} className="flex flex-wrap items-center gap-1.5 text-sm text-foreground">
                <span>{t('perfilCargo.duplicados.grupo', {nombres: grupo.nombres.join(' · ')})}</span>
                <PorQue
                  label={t('common.deDondeSalio')}
                  ariaLabel={`${t('common.deDondeSalio')} — ${grupo.nombres.join(' · ')}`}
                  testId={`duplicado-descartes-${grupo.clave.join('-')}`}>
                  <p>{t('perfilCargo.descartoPalabras', {palabras: grupo.palabras_descartadas.join(', ')})}</p>
                </PorQue>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-sm text-muted-foreground">{t('perfilCargo.duplicados.advertencia')}</p>
          {/* Unir cargos se hace en «Cargos y áreas» (TagsManagerDialog), que se abre desde Empleados. */}
          <Link className="text-sm underline" to={`/businesses/${businessId}/employees`}>
            {t('perfilCargo.duplicados.enlace')}
          </Link>
        </section>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={table.searchInput}
          onChange={table.setSearchInput}
          placeholder={t('perfilCargo.buscar')}
          className="max-w-sm"
          data-testid="propuestas-buscar"
        />
        <PerfilesCargoFiltro
          filtro={filtro}
          onFiltro={(siguiente) => {
            setFiltro(siguiente)
            table.setPage(1)
          }}
          pendientes={cabecera.pendientes}
          total={cabecera.total_cargos}
        />
      </div>

      {!lista.data && <Skeleton className="h-40 w-full" />}
      {lista.data && (
        <>
          {lista.data.items.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="propuestas-vacio">
              {t('perfilCargo.vacioFiltroPropuestas')}
            </p>
          )}
          <div className="space-y-3">
            {lista.data.items.map((fila) => (
              <PropuestaCargo
                key={fila.position_id}
                fila={fila}
                disponibles={cabecera.perfiles_disponibles}
                puedeAplicar={puedeAplicar}
                pendiente={aplicar.isPending}
                onUsar={(accion, catalogoKey) => usar(fila.position_id, accion, catalogoKey)}
              />
            ))}
          </div>
          {/* `table.page` y no `lista.data.page`: con la de la respuesta el pie se queda en la
              página anterior mientras llega la nueva. */}
          <PaginationBar
            page={table.page}
            pageSize={PROPUESTAS_PAGE_SIZE}
            total={lista.data.total}
            onPageChange={table.setPage}
          />
        </>
      )}

      {/* El catálogo va al FINAL: es referencia, no trabajo. */}
      <CatalogoKairos catalogo={cabecera.catalogo} />
    </div>
  )
}
